import type { PropsWithChildren, ReactElement } from 'react';
import { useCallback, useRef } from 'react';

import { useOnDetailsToggle } from '#/components/Details/hooks/useOnDetailsToggle';
import { useRichInitialDetailsStateClosedClassRef } from '#/snippets/rich-details-snippet';
import { richDetailsAttr } from '#/snippets/snippet-attributes';

import { AccordionDetails } from './AccordionItem.styled';

export type Props = {
  defaultOpen?: boolean;
  header?: ReactElement<'summary'>;
  qaId?: string;
  className?: string;
  id?: string;
  name?: string;
  onToggle?: (open: boolean) => void;
};

export function AccordionItem({
  defaultOpen,
  children,
  className,
  header,
  name,
  id,
  onToggle,
  qaId,
}: PropsWithChildren<Props>) {
  const detailsElRef = useRef<HTMLDetailsElement>(null);

  const toggle = useCallback(
    (open: boolean) => {
      onToggle?.(open);
    },
    [onToggle],
  );

  useOnDetailsToggle({ detailsElRef, onToggle: toggle });

  return (
    <AccordionDetails
      id={id}
      name={name}
      className={[className, useRichInitialDetailsStateClosedClassRef().current]
        .filter(Boolean)
        .join(' ')}
      ref={detailsElRef}
      open={defaultOpen}
      data-qa-id={qaId}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...{ [richDetailsAttr]: true }}
      suppressHydrationWarning
    >
      {header}
      {children}
    </AccordionDetails>
  );
}
